import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { img } from "../lib/format";
import SEO from "../components/SEO";
import { meetingTimeLabel } from "../lib/enquiryContext";

export default function OrderConfirmation() {
  const { state } = useLocation();
  if (!state?.reference) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-32 text-center" data-testid="enquiry-confirmation-missing">
        <SEO title="Enquiries — S H & SONS ®" description="Contact the S H & SONS ® export team." />
        <h1 className="text-3xl font-light text-charcoal">Speak with our export team</h1>
        <p className="mt-5 text-[14px] text-mocha">If you have already submitted an enquiry, keep your reference for follow-up.</p>
        <Link to="/enquiry" className="pill-dark mt-8">Make an enquiry</Link>
      </div>
    );
  }
  const items = state.items || [];
  const meeting = state.meeting;
  const isBooked = meeting?.meeting_status === "booked";
  const meetingTime = meetingTimeLabel(meeting?.meeting_slot, meeting?.meeting_ends_at, meeting?.meeting_time_zone || "Europe/Berlin");
  const emailLink = "mailto:hello@shandsons.in?subject=" + encodeURIComponent("Export enquiry " + state.reference) + "&body=" + encodeURIComponent("Hello S H & SONS ®,\n\nI am following up on my website enquiry " + state.reference + ".\n");
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-8 py-16 lg:py-24" data-testid="enquiry-confirmation-page">
      <SEO title="Enquiry Received — S H & SONS ®" description="Your export enquiry has been received." />
      <div className="text-center">
        <img src="/assets/monogram-black.svg" alt="S H & SONS ® monogram" className="h-14 mx-auto mb-6" />
        <CheckCircle2 className="w-8 h-8 mx-auto text-brass" strokeWidth={1.25} />
        <h1 className="mt-6 h-display text-4xl sm:text-6xl">{isBooked ? "Your meeting is booked" : "Thank you for your enquiry"}</h1>
        <p className="mt-4 text-[14px] text-mocha leading-relaxed">Your enquiry has been received in our enquiry system.</p>
        {state.email_status === "sent" && <p className="mt-3 text-[13px] text-mocha">An email notification has been sent to our team.</p>}
        {state.email_status === "failed" && <p className="mt-3 text-[13px] text-mocha">Your enquiry is saved, but its email notification could not be sent. You can contact our team using the email link below.</p>}
        {state.email_status !== "sent" && state.email_status !== "failed" && <p className="mt-3 text-[13px] text-mocha">You can also compose an email to our team, quoting the reference below.</p>}
        <p className="mt-6 inline-block rounded-full border border-line px-6 py-3 text-xs uppercase tracking-[0.25em]" data-testid="enquiry-reference">Reference {state.reference}</p>
      </div>
      {(items.length > 0 || state.catalogue_requested || state.context || meeting) && (
        <div className="mt-14 border border-line rounded-[28px] p-6 sm:p-8" data-testid="enquiry-details">
          <h2 className="text-[20px] font-light mb-5">Your request</h2>
          {meetingTime ? <p className="text-[14px] mb-6 text-charcoal">Ambiente 2027 · {meetingTime}<span className="block mt-2 text-[12px] text-mocha">Frankfurt local time · Hall 10.1, Stand C06</span></p> : state.context ? <p className="text-[13px] mb-6 text-mocha">{state.context}</p> : null}
          <div className="space-y-4">
            {items.map((item) => (
              <div key={item.slug} className="flex gap-4 items-center">
                <div className="w-16 h-16 bg-cream rounded-2xl shrink-0 flex items-center justify-center">{item.image && <img src={img(item.image)} alt={item.name} className="w-full h-full object-contain p-2" />}</div>
                <div><p className="text-[14px]">{item.name}</p><p className="text-[12px] text-mocha mt-1">{item.quantity == null ? "Quantity to discuss" : "Approximate quantity: " + item.quantity}</p></div>
              </div>
            ))}
            {state.catalogue_requested && <p className="text-[13px] text-mocha">Export catalogue requested</p>}
          </div>
          <p className="mt-6 pt-5 border-t border-line text-[12px] text-mocha">Specifications, quantities and timelines are confirmed directly with our team.</p>
        </div>
      )}
      <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
        <a href={emailLink} className="pill-dark px-7" data-testid="enquiry-compose-email">Compose email <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} /></a>
        <Link to="/shop" className="pill-outline px-7">Explore the collection</Link>
      </div>
    </div>
  );
}
